/**
 * Settings + system data hooks (frontend.md §5 step 12).
 *
 * `useSettingsQuery` / `useUpdateSettingsMutation` back the Settings surface — including
 * the D-007 write-back toggle and the S4 owner-entity. The update mutation does an
 * optimistic write (frontend.md §2.2) then invalidates to reconcile with the server.
 *
 * `useApiKeysMutation` is write-only (ADR 008 — keys go in, never come out); there is no
 * corresponding query. `useCostTelemetryQuery` and `useSystemStatusQuery` back the
 * cost panel and (separately) the global status bar.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchCostTelemetry,
  fetchSettings,
  fetchSystemStatus,
  putApiKeys,
  putSettings,
} from '@/api';
import type {
  CostTelemetryReport,
  SettingsUpdate,
  SettingsView,
  SourceActionResult,
  SystemStatus,
} from '@/types';
import { queryKeys } from '@/providers';
import { useErrorHandler } from './useErrorHandler';
import { RENDER_DEFAULTS } from '@/config/renderDefaults';

export function useSettingsQuery(): UseQueryResult<SettingsView, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.settings(),
    queryFn: async () => {
      try {
        return await fetchSettings();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useUpdateSettingsMutation(): UseMutationResult<
  SettingsView,
  Error,
  SettingsUpdate,
  { previous: SettingsView | undefined }
> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: (update: SettingsUpdate) => putSettings(update),
    // Optimistic update — frontend.md §2.2. The settings panel feels instant; the
    // server response then reconciles via invalidation.
    onMutate: async (update) => {
      await qc.cancelQueries({ queryKey: queryKeys.settings() });
      const previous = qc.getQueryData<SettingsView>(queryKeys.settings());
      if (previous) {
        const optimisticValues = { ...previous.values };
        if (update.joplinWriteBackEnabled !== undefined) {
          optimisticValues['joplin_write_back_enabled'] = update.joplinWriteBackEnabled;
        }
        if (update.backfillMode !== undefined) {
          optimisticValues['backfill_mode'] = update.backfillMode;
        }
        if (update.llmSpendCapUsd !== undefined) {
          optimisticValues['llm_spend_cap_usd'] = update.llmSpendCapUsd;
        }
        if (update.joplinPollIntervalMinutes !== undefined) {
          optimisticValues['joplin_poll_interval_minutes'] = update.joplinPollIntervalMinutes;
        }
        if (update.inferenceDevice !== undefined) {
          optimisticValues['inference_device'] = update.inferenceDevice;
        }
        if (update.backgroundExtractionEnabled !== undefined) {
          optimisticValues['background_extraction_enabled'] =
            update.backgroundExtractionEnabled;
        }
        if (update.extractionMode !== undefined) {
          optimisticValues['extraction_mode'] = update.extractionMode;
        }
        if (update.ownerUserName !== undefined) {
          // Phase 8 — null clears the name (Settings UI sends null on empty input).
          optimisticValues['owner_user_name'] = update.ownerUserName ?? '';
        }
        qc.setQueryData<SettingsView>(queryKeys.settings(), {
          ownerEntityId:
            update.ownerEntityId !== undefined ? update.ownerEntityId : previous.ownerEntityId,
          values: optimisticValues,
        });
      }
      return { previous };
    },
    onError: (err, _update, context) => {
      // Roll the optimistic write back, then route the error.
      if (context?.previous) qc.setQueryData(queryKeys.settings(), context.previous);
      handleError(err);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.settings() });
    },
  });
}

export function useApiKeysMutation(): UseMutationResult<
  SourceActionResult,
  Error,
  Record<string, string>
> {
  const { handleError } = useErrorHandler();
  return useMutation({
    // Write-only (ADR 008) — no query to invalidate; keys never come back out.
    mutationFn: (keys: Record<string, string>) => putApiKeys(keys),
    onError: (err) => handleError(err),
  });
}

export function useCostTelemetryQuery(): UseQueryResult<CostTelemetryReport, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.costTelemetry(),
    queryFn: async () => {
      try {
        return await fetchCostTelemetry();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

/**
 * `useSystemStatusQuery` — the lightweight poll behind `<GlobalStatusBar>` (frontend.md
 * §2.1, §2.4). Polls on the capability-tiered interval; always enabled (the status bar is
 * always mounted in the shell).
 */
export function useSystemStatusQuery(): UseQueryResult<SystemStatus, Error> {
  const { pollIntervalMs } = RENDER_DEFAULTS;
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.systemStatus(),
    refetchInterval: pollIntervalMs,
    queryFn: async () => {
      try {
        return await fetchSystemStatus();
      } catch (err) {
        // The status bar degrades quietly — handleError logs it; the bar shows "offline".
        handleError(err);
        throw err;
      }
    },
  });
}
